import React, { Component } from 'react';
import { compose } from 'redux';
import { times, get, isEmpty, keys } from 'lodash';
import memoize from 'memoize-one';
import Select from 'components/forms/Select';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import LoadingIcon from 'mdi-react/LoadingIcon';
import NotificationSystem from 'rc-notification';
import { Grid, Typography } from '@material-ui/core';
import { Field, reduxForm, getFormValues, change, FormSection } from 'redux-form';
import RenderFormRadio from 'components/forms/RenderFormRadio';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';

import RenderSelectInput from 'components/forms/RenderSelectInput';
import { INCLUDE_EXCLUDE_STATUSES, OPPORTUNITY_STAGES, NOTIFICATION_NODE_STYLE } from 'constants/misc';
import { HEADERS_SECTION } from 'constants/contact';
import { resultsToOptions } from 'utils/misc';
import { handleBlur } from 'utils/validators';
import { getCsvHeaders } from 'redux/actions/csvActions';
import { getCsvTemplateFields } from 'redux/actions/csvTemplateFieldActions';
import { getCsvTemplates } from 'redux/actions/csvTemplateActions';
import BasicNotification from 'components/Notification';
import FormLabel from 'components/forms/FormLabel';
import {
  getMappingFromFormData,
  getContactBasicFields,
  getContactOtherFields,
  getFieldName,
} from 'utils/contact';
import CsvFieldMap from './CsvMapFields';

class ContactsTemplatePage extends Component {
  static propTypes = {
    sequence: PropTypes.shape({
      id: PropTypes.number,
      organization: PropTypes.number,
      account_id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    contacts: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      account: PropTypes.number,
      sequence: PropTypes.number,
    }).isRequired,
    handleSubmit: PropTypes.func.isRequired,
    headersCSV: PropTypes.shape({
      header: PropTypes.string.isRequired,
      sample_entry_value: PropTypes.string.isRequired,
    }).isRequired,
    contactsCSV: PropTypes.shape({}).isRequired,
    getCsvTemplates: PropTypes.func.isRequired,
    getCsvTemplateFields: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    formValues: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    csvTemplates: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    csv: PropTypes.shape({
      sequence: PropTypes.number,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      headers: PropTypes.array,
    }).isRequired,
    csvTemplateFields: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
  };

  static defaultProps = {
    formValues: {},
  };

  constructor(props) {
    super(props);
    this.state = { showOtherFields: false };
    this.toggleShowOtherFields = this.toggleShowOtherFields.bind(this);
  }

  async componentDidMount() {
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({ style: { zIndex: '9999' } }, n => this.notification = n);
    this.props.getCsvTemplateFields();
  }

  componentWillUnmount() {
    this.notification.destroy();
  }

  toggleShowOtherFields() {
    this.setState({ showOtherFields: !this.state.showOtherFields });
  }

  handleTemplateChange = async (template) => {
    if (template) {
      // reset form fields
      this.props.reset();
      // notify redux-form about value change
      this.props.dispatch(change('sequenceImportTemplateForm', 'mappingTemplate', template));
    } else {
      // if no template selected then reset form and skip mapping
      this.props.reset();
      return;
    }
    const selectedTemplate = this.props.csvTemplates.results.find(tmplt => tmplt.id === template.value);
    const mappedFields = selectedTemplate.csvimporttemplatefieldvalue_set;
    const csvHeaders = this.props.headersCSV;
    const templateFields = this.props.csvTemplateFields;

    mappedFields.forEach((mField) => {
      const { column, column_value: columnMappedValue } = mField;
      const isMappedFieldPresent = templateFields.results.find(tField => tField.id === column.id);
      const mappedHeader = csvHeaders.find(header => header.header === columnMappedValue);
      if (isMappedFieldPresent && mappedHeader) {
        this.props.dispatch(change(
          'sequenceImportTemplateForm',
          `${HEADERS_SECTION}.${getFieldName(column)}`,
          { value: mappedHeader.sample_entry_value, label: mappedHeader.header },
        ));
      }
    });
  }

  handleGetOptions = async (search) => {
    await this.props.getCsvTemplates({ search });
    return resultsToOptions(this.props.csvTemplates.results, 'name') || [];
  }

  showNotification = (color, message) => {
    this.notification.notice({
      content: <BasicNotification
        color={color}
        message={message}
      />,
      duration: 4,
      closable: false,
      style: NOTIFICATION_NODE_STYLE,
      className: 'alert--colored right-up',
    });
  }

  renderHeaderOptions = memoize((headers, formValues) => {
    if (isEmpty(headers)) return [];
    const selectedHeaders = keys(getMappingFromFormData(get(formValues, 'headers', {})));
    return headers.map(header => ({ value: header.sample_entry_value, label: header.header }))
      .filter(r => !selectedHeaders.includes(r.label));
  });

  render() {
    const {
      handleSubmit, csv,
      csvTemplateFields, csvTemplates,
      contacts, formValues,
    } = this.props;
    const renderedHeaders = this.renderHeaderOptions(this.props.headersCSV, formValues);
    const isLoading = csv.loading || csvTemplateFields.loading || csvTemplates.loading || contacts.loading;

    return (
      <form
        className="instance-form sequence-create__template-page mt-4"
        onSubmit={handleSubmit}
      >
        {isLoading && <div className="loader"><LoadingIcon /></div>}

        <div className="instance-form__body">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label htmlFor="mappingTemplate">Select Template to Map Fields</label>
          <Field
            name="mappingTemplate"
            onBlur={handleBlur}
            placeholder="Template name"
            component={RenderSelectInput}
            getOptions={this.handleGetOptions}
            handleChange={this.handleTemplateChange}
            isClearable
          />
          <Grid container className="pl-3 pr-3 mt-2">
            <Grid item sm={5} className="map-from">
              {/* eslint-disable-next-line jsx-a11y/label-has-for */}
              <label>PR Volt Fields</label>
            </Grid>
            <Grid item sm={1} />
            <Grid item sm={6} className="map-to">
              {/* eslint-disable-next-line jsx-a11y/label-has-for */}
              <label>CSV File Fields</label>
            </Grid>
          </Grid>
          <FormSection name={HEADERS_SECTION} component="div" className="template-rows">
            {csvTemplateFields.loaded ? (
              <>
                {getContactBasicFields(csvTemplateFields.results).map(csvField => (
                  <CsvFieldMap csvField={csvField} renderedHeaders={renderedHeaders} values={formValues} />
                ))}
                <div className="field-seperator">
                  <Typography variant="span" onClick={this.toggleShowOtherFields}>
                    Hide Other Fields
                    {this.state.showOtherFields ?
                      <ChevronUpIcon /> :
                      <ChevronDownIcon />
                    }
                  </Typography>
                  <hr className="w-100" />
                </div>
                {this.state.showOtherFields && getContactOtherFields(csvTemplateFields.results).map(csvField => (
                  <CsvFieldMap csvField={csvField} renderedHeaders={renderedHeaders} values={formValues} />
                ))}
              </>
            ) : (
              times(6, i => (
                <Grid container key={i} className="template-row placeholder">
                  <Grid item className="map-from"><p /><p /></Grid>
                  <Grid item className="map-arrow" />
                  <Grid item className="map-to"><div /></Grid>
                </Grid>
              ))
            )}
          </FormSection>

          <p className="contacts__header mt-4">
            <span>(optional) </span>
            <span className="bold-header">Include or exclude existing contacts</span>
          </p>

          <div className="mt-2">
            <p className="contacts__header mb-2">Based on their most recent inbox category</p>
            <FormLabel className="mt-0">
              <Field
                name="replyRadio"
                id="include"
                component={RenderFormRadio}
                type="radio"
                value="include"
              />
              <span>Include</span>
            </FormLabel>
            <FormLabel className="mt-0">
              <Field
                name="replyRadio"
                id="exclude"
                component={RenderFormRadio}
                type="radio"
                value="exclude"
              />
              <span className="radio-btn__label">Exclude</span>
            </FormLabel>
          </div>
          <div>
            <Field
              name="replyStatusField"
              component={propsCus => (
                <Select
                  placeholder="Select a reply status"
                  isMulti
                  value={propsCus.input.value}
                  onChange={propsCus.input.onChange}
                  onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                  options={INCLUDE_EXCLUDE_STATUSES || []}
                />
              )}
            />
          </div>

          <div className="mt-4">
            <p className="contacts__header mb-2 mt-0">Based on their opportunity stage</p>
            <FormLabel className="mt-0">
              <Field
                name="stageRadio"
                id="include"
                component={RenderFormRadio}
                type="radio"
                value="include"
              />
              <span>Include</span>
            </FormLabel>
            <FormLabel className="mt-0">
              <Field
                name="stageRadio"
                id="exclude"
                component={RenderFormRadio}
                type="radio"
                value="exclude"
              />
              <span className="radio-btn__label">Exclude</span>
            </FormLabel>
          </div>
          <div>
            <Field
              name="opportunityStageField"
              component={propsCus => (
                <Select
                  placeholder="Select an opportunity stage"
                  isMulti
                  value={propsCus.input.value}
                  onChange={propsCus.input.onChange}
                  onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                  options={OPPORTUNITY_STAGES || []}
                />
              )}
            />
          </div>
        </div>
      </form>
    );
  }
}


export default compose(
  connect(
    state => ({
      headersCSV: state.contactsCSV.headers,
      contactsCSV: state.contactsCSV.contacts,
      sequence: state.sequence,
      csv: state.csv,
      contacts: state.contacts,
      csvTemplates: state.csvTemplates,
      csvTemplateFields: state.csvTemplateFields,
      formValues: getFormValues('sequenceImportTemplateForm')(state),
      initialValues: {
        replyStatusField: [],
        opportunityStageField: [],
        replyRadio: 'include',
        stageRadio: 'include',
      },
    }),
    {
      getCsvHeaders,
      getCsvTemplateFields,
      getCsvTemplates,
    },
  ),
  reduxForm({
    form: 'sequenceImportTemplateForm',
    onSubmitFail: (errors) => {
      if (!isEmpty(get(errors, HEADERS_SECTION))) {
        NotificationSystem.newInstance({}, (notification) => {
          notification.notice({
            content: <BasicNotification
              color="danger"
              message="Form has errors, see missing fields above."
            />,
            duration: 4,
            closable: false,
            style: NOTIFICATION_NODE_STYLE,
            className: 'alert--colored right-up',
          });
        });
      }
    },
  }),
)(ContactsTemplatePage);
